/* eslint-disable no-unused-vars */
import React, {useEffect, useCallback, useRef} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {useFormik} from 'formik'
import {Link} from 'react-router-dom'
import * as Yup from 'yup'

import {detailPageUrl, DetailPage, ExpenseDetailPage} from '../../../helpers'
import {KTCard, KTCardBody, Loading, Datepicker} from '../../../components'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {checkGetId, checkUpdate} from '../../../redux/slices/check.slice'
import {useMemoizedValidationSchema, useMemoizedFormValues} from '../../../utils/performanceOptimizations'

const selectCheckData = (state) => ({
  isRedirect: state.check.isRedirect,
  detail: state.check.detail,
  loading: state.check.loading
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = location.pathname.split('/')

  const {isRedirect, detail, loading} = useSelector(selectCheckData)
  const dispatch = useDispatch()

  const formikRef = useRef(null)
  const isFormPopulatedRef = useRef(false)

  useEffect(() => {
    dispatch(checkGetId(id))
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Description: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Id: '',
    Description: '',
    EditDate: new Date(),
    DefermentDate: new Date(),
  })

  const handleSubmit = useCallback(async (values, {setSubmitting}) => {
    setSubmitting(true)
    dispatch(checkUpdate(values))
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  formikRef.current = formik

  useEffect(() => {
    if (detail && formikRef.current && !isFormPopulatedRef.current) {
      for (const property in detail) {
        const propVal = property.charAt(0).toUpperCase() + property.slice(1)
        for (const values in formValues) {
          if (values === propVal) {
            formikRef.current.setFieldValue(
              propVal,
              property === 'editDate' || property === 'defermentDate'
                ? new Date(detail[property])
                : detail[property]
            )
          }
        }
      }
      isFormPopulatedRef.current = true
    }
  }, [detail, formValues])

  const breadcrumbs = [
    {
      title: intl.formatMessage({id: 'BC.CHECKS'}),
      path: '/cash/checks',
      isSeparator: false,
      isActive: false,
    },
    {
      title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
      path: `/cash/checks/detail/${id}`,
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ]

  useEffect(() => {
    if (isRedirect) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)
  }, [isRedirect, navigate, pageUrl, id])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>{intl.formatMessage({id: 'BC.CHECKS.EDIT'})}</PageTitle>
      <KTCard>
        <KTCardBody>
          {detail && (
            <form className='form' onSubmit={formik.handleSubmit} noValidate>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'CHECK.GET.DESCRIPTION'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <input
                    type='text'
                    required
                    className='form-control form-control-solid mb-3 mb-lg-0'
                    autoComplete='off'
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps('Description')}
                  />
                  {formik.touched.Description && formik.errors.Description && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.Description}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'CHECK.GET.EDITDATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    {...formik.getFieldProps('EditDate')}
                    onChange={([date]) => formik.setFieldValue('EditDate', date)}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                  {intl.formatMessage({id: 'CHECK.GET.DEFERMENTDATE'})}
                </label>
                <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                  <Datepicker
                    {...formik.getFieldProps('DefermentDate')}
                    onChange={([date]) => formik.setFieldValue('DefermentDate', date)}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'CHECK.GET.BANK'})}
                </label>
                <div className='col-lg-6 fv-row'>
                  <span className='fw-bold fs-6'>{detail && detail.bankName}</span>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'CHECK.GET.CHECKNO'})}
                </label>
                <div className='col-lg-6 fv-row'>
                  <span className='fw-bold fs-6'>{detail && detail.checkNo}</span>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'CHECK.GET.PROJECTNAME'})}
                </label>
                <div className='col-lg-6 fv-row'>
                  <Link
                    to={detailPageUrl(DetailPage.Project, detail && detail.projectId)}
                    className='text-dark fw-bold d-block fs-6 text-hover-primary'
                    target='_blank'
                  >
                    {detail && detail.projectName}
                  </Link>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'CHECK.GET.TOTALPRICE'})}
                </label>
                <div className='col-lg-6 fv-row'>
                  <span className='fw-bold fs-6'>
                    {intl.formatNumber(detail && detail.totalPrice, {style: 'currency', currency: 'try', minimumFractionDigits: 2, maximumFractionDigits: 4})}
                  </span>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'CHECK.GET.CHECKTYPE'})}
                </label>
                <div className='col-lg-6 fv-row'>
                  <span className='fw-bold fs-6'>{detail && detail.checkTypeName}</span>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'CHECK.GET.CHECKSTATE'})}
                </label>
                <div className='col-lg-6 fv-row'>
                  <span className='fw-bold fs-6'>{detail && detail.checkStateName}</span>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'CHECK.GET.FROMCOMPANY'})}
                </label>
                <div className='col-lg-6 fv-row'>
                  <span className='fw-bold fs-6'>
                    <Link
                      to={detailPageUrl(DetailPage.CompanySupplier, detail && detail.fromCompanyId)}
                      className='text-dark fw-bold d-block fs-6 text-hover-primary'
                      target='_blank'
                    >
                      {detail && detail.fromCompanyName}
                    </Link>
                  </span>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'CHECK.GET.TOCOMPANY'})}
                </label>
                <div className='col-lg-6 fv-row'>
                  <span className='fw-bold fs-6'>
                    <Link
                      to={detailPageUrl(DetailPage.CompanySupplier, detail && detail.toCompanyId)}
                      className='text-dark fw-bold d-block fs-6 text-hover-primary'
                      target='_blank'
                    >
                      {detail && detail.toCompanyName}
                    </Link>
                  </span>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'CHECK.GET.SALEINVOICE'})}
                </label>
                <div className='col-lg-6 fv-row'>
                  <span className='fw-bold fs-6'>
                    <Link
                      to={detailPageUrl(DetailPage.SaleInvoice, detail && detail.saleInvoiceId)}
                      className='text-dark fw-bold d-block fs-6 text-hover-primary'
                      target='_blank'
                    >
                      {detail && detail.saleInvoiceName}
                    </Link>
                  </span>
                </div>
              </div>
              <div className='row mb-6'>
                <label className='col-lg-3 fw-bold fs-6 mb-2'>
                  {intl.formatMessage({id: 'CHECK.GET.EXPENSEINVOICE'})}
                </label>
                <div className='col-lg-6 fv-row'>
                  <span className='fw-bold fs-6'>
                    <Link
                      to={detailPageUrl(ExpenseDetailPage[1], detail && detail.expenseInvoiceId)}
                      className='text-dark fw-bold d-block fs-6 text-hover-primary'
                      target='_blank'
                    >
                      {detail && detail.expenseInvoiceName}
                    </Link>
                  </span>
                </div>
              </div>

              <div className='row pt-15'>
                <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                  <button
                    className='btn btn-light'
                    onClick={() => navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${id}`)}
                  >
                    {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                  </button>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    data-kt-users-modal-action='submit'
                    disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                  >
                    <span className='indicator-label'>
                      {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                    </span>
                    {formik.isSubmitting && (
                      <span className='indicator-progress'>
                        {intl.formatMessage({id: 'PLEASE'})}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {Edit}
